/**
 * AI プロンプト管理・分析履歴機能の型定義
 */

import { Timestamp } from 'firebase/firestore';

/**
 * 評価基準の重み付け
 */
export interface PromptWeights {
  /** 遺伝的多様性（0-100） */
  geneticDiversity: number;
  /** 繁殖実績（0-100） */
  breedingHistory: number;
  /** 特徴マッチング（0-100） */
  traitMatch: number;
}

/**
 * 近親係数の閾値設定
 */
export interface InbreedingThresholds {
  /** 安全ゾーン上限（デフォルト: 6.25） */
  safe: number;
  /** 注意ゾーン上限（デフォルト: 12.5） */
  caution: number;
  /** 警告ゾーン上限（デフォルト: 25） */
  warning: number;
}

/**
 * AIプロンプト設定
 */
export interface AIPrompt {
  /** プロンプトID */
  id: string;
  /** 作成者のUID */
  userId: string;
  /** プロンプト名 */
  name: string;
  /** 説明文 */
  description: string;
  /** バージョン番号（1から開始、編集毎にインクリメント） */
  version: number;
  /** 有効フラグ */
  isActive: boolean;
  /** デフォルトフラグ（ユーザーごとに1つのみtrue） */
  isDefault: boolean;
  /** 評価基準の重み付け */
  weights: PromptWeights;
  /** 近親係数の閾値設定 */
  inbreedingThresholds: InbreedingThresholds;
  /** カスタム指示（上級者向け、オプション） */
  customInstructions?: string;
  /** 作成日時 */
  createdAt: Timestamp;
  /** 更新日時 */
  updatedAt: Timestamp;
}

/**
 * AIプロンプト作成用の入力データ（id, createdAt, updatedAtは自動生成）
 */
export type AIPromptInput = Omit<AIPrompt, 'id' | 'createdAt' | 'updatedAt'>;

/**
 * AIプロンプト更新用の部分データ
 */
export type AIPromptUpdate = Partial<Omit<AIPrompt, 'id' | 'createdAt' | 'updatedAt' | 'userId'>>;

/**
 * AIプロンプトの変更履歴
 */
export interface AIPromptHistory {
  /** 履歴ID */
  id: string;
  /** 元のプロンプトID */
  promptId: string;
  /** 所有者のUID（Firestoreセキュリティルール用） */
  userId: string;
  /** バージョン番号 */
  version: number;
  /** その時点のプロンプト全体のスナップショット */
  snapshot: AIPrompt;
  /** 変更者のUID */
  changedBy: string;
  /** 変更理由（オプション） */
  changeNote?: string;
  /** 作成日時 */
  createdAt: Timestamp;
}

/**
 * AI分析の入力データ
 */
export interface AIAnalysisInput {
  /** 対象メス犬のID */
  femaleId: string;
  /** 対象メス犬の名前 */
  femaleName: string;
  /** 候補オス犬のIDリスト */
  candidateIds: string[];
  /** 候補オス犬の名前リスト */
  candidateNames: string[];
}

/**
 * AI分析の提案結果
 */
export interface AIAnalysisSuggestion {
  /** オス犬のID */
  maleId: string;
  /** オス犬の名前 */
  maleName: string;
  /** スコア（0-100） */
  score: number;
  /** 推奨理由 */
  reasons: string[];
  /** 警告事項（オプション） */
  warnings?: string[];
}

/**
 * AI分析の出力データ
 */
export interface AIAnalysisOutput {
  /** 提案リスト */
  suggestions: AIAnalysisSuggestion[];
  /** 生のAIレスポンス（デバッグ用） */
  rawResponse?: string;
}

/**
 * AI分析の履歴
 */
export interface AIAnalysisHistory {
  /** 履歴ID */
  id: string;
  /** 実行者のUID */
  userId: string;
  /** 使用したプロンプトID */
  promptId: string;
  /** 使用したプロンプトのバージョン */
  promptVersion: number;
  /** 入力データ */
  input: AIAnalysisInput;
  /** 出力データ */
  output: AIAnalysisOutput;
  /** 実行時間（ミリ秒） */
  executionTime: number;
  /** ステータス */
  status: 'success' | 'error';
  /** エラーメッセージ（エラー時） */
  errorMessage?: string;
  /** 作成日時 */
  createdAt: Timestamp;
}

/**
 * AI分析履歴作成用の入力データ
 */
export type AIAnalysisHistoryInput = Omit<AIAnalysisHistory, 'id' | 'createdAt'>;

/**
 * AI分析履歴のフィルター条件
 */
export interface AIAnalysisHistoryFilters {
  /** 開始日 */
  startDate?: Date;
  /** 終了日 */
  endDate?: Date;
  /** メス犬のID */
  femaleId?: string;
  /** ステータス */
  status?: 'success' | 'error';
}

/**
 * ページネーション用のオプション
 */
export interface PaginationOptions {
  /** 1ページあたりの件数 */
  limit: number;
  /** 開始位置（前回取得の最後のドキュメント） */
  startAfterDoc?: unknown;
}

/**
 * デフォルトのプロンプト設定値
 */
export const DEFAULT_PROMPT_WEIGHTS: PromptWeights = {
  geneticDiversity: 40,
  breedingHistory: 30,
  traitMatch: 30,
};

/**
 * デフォルトの近親係数閾値
 */
export const DEFAULT_INBREEDING_THRESHOLDS: InbreedingThresholds = {
  safe: 6.25,
  caution: 12.5,
  warning: 25,
};

/**
 * 重み付けの合計が100かどうかを検証
 */
export function validateWeightsTotal(weights: PromptWeights): boolean {
  const total = weights.geneticDiversity + weights.breedingHistory + weights.traitMatch;
  return total === 100;
}

/**
 * 閾値の順序が正しいかどうかを検証（safe < caution < warning）
 */
export function validateThresholdsOrder(thresholds: InbreedingThresholds): boolean {
  return thresholds.safe < thresholds.caution && thresholds.caution < thresholds.warning;
}
